import { useState } from 'react';
import { PRICING, EXTRAS, AIRPORTS } from '../../../config/pricing';

export default function PriceCalculator() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState<'schiphol' | 'rotterdam' | 'eindhoven' | ''>('');
  const [passengers, setPassengers] = useState(1);
  const [stationWagon, setStationWagon] = useState(false);
  const [childSeats, setChildSeats] = useState(0);
  const [extraStop, setExtraStop] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const calculatePrice = () => {
    if (!from || !to) return 0;

    const basePrice = PRICING[from]?.[to] || 0;
    if (!basePrice) return 0;

    let total = basePrice;

    if (stationWagon) total += EXTRAS.stationwagon;
    if (childSeats > 0) total += EXTRAS.childSeat * childSeats;
    if (extraStop) total += EXTRAS.extraStop;

    return total;
  };

  const totalPrice = calculatePrice();

  const handleBookingSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d587djh3kamldd155ulg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        
        setTimeout(() => {
          window.location.assign('/?success=1');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Formulier fout:', error);
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const getAirportDisplayName = (key: string) => {
    if (key === 'schiphol') return 'Schiphol Airport';
    if (key === 'rotterdam') return 'Rotterdam The Hague Airport';
    if (key === 'eindhoven') return 'Eindhoven Airport';
    return key;
  };

  return (
    <>
      <section id="prijzen" className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Bereken direct uw vaste taxiprijs
            </h2>
            <p className="text-base sm:text-lg text-slate-600">
              Binnen 30 seconden weet u precies waar u aan toe bent.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-10">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Vertreklocatie
                  </label>
                  <select
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  >
                    <option value="">Selecteer vertrekplaats</option>
                    {Object.keys(PRICING).map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Bestemming
                  </label>
                  <select
                    value={to}
                    onChange={(e) => setTo(e.target.value as any)}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  >
                    <option value="">Selecteer luchthaven</option>
                    {Object.entries(AIRPORTS).map(([key, name]) => (
                      <option key={key} value={key}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Aantal Passagiers
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={passengers}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      setPassengers(isNaN(value) || value < 1 ? 1 : Math.min(value, 8));
                    }}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="space-y-3 pt-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stationWagon}
                      onChange={(e) => setStationWagon(e.target.checked)}
                      className="w-5 h-5 text-amber-500 rounded focus:ring-amber-500"
                    />
                    <span className="text-sm text-slate-700">
                      Stationwagon (+€7,50)
                    </span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={extraStop}
                      onChange={(e) => setExtraStop(e.target.checked)}
                      className="w-5 h-5 text-amber-500 rounded focus:ring-amber-500"
                    />
                    <span className="text-sm text-slate-700">
                      Extra tussenstop (+€7,50)
                    </span>
                  </label>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Aantal Kinderzitjes (€5,00 per stuk)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="3"
                      value={childSeats}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setChildSeats(isNaN(value) || value < 0 ? 0 : Math.min(value, 3));
                      }}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-between">
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-8 mb-6">
                  <div className="text-center mb-6">
                    <p className="text-sm font-semibold text-slate-600 mb-2">
                      Totaalprijs
                    </p>
                    <p className="text-5xl font-bold text-slate-900">
                      €{totalPrice.toFixed(2)}
                    </p>
                    <p className="text-sm text-slate-600 mt-2">All-in, geen verborgen kosten</p>
                  </div>

                  {totalPrice > 0 && (
                    <div className="space-y-2 text-sm text-slate-700 border-t border-amber-200 pt-4">
                      <div className="flex justify-between">
                        <span>Route:</span>
                        <span className="font-semibold">{from} → {getAirportDisplayName(to)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Passagiers:</span>
                        <span className="font-semibold">{passengers}</span>
                      </div>
                      {stationWagon && (
                        <div className="flex justify-between">
                          <span>Stationwagon:</span>
                          <span className="font-semibold">+€7,50</span>
                        </div>
                      )}
                      {childSeats > 0 && (
                        <div className="flex justify-between">
                          <span>Kinderzitjes ({childSeats}x):</span>
                          <span className="font-semibold">+€{(childSeats * 5).toFixed(2)}</span>
                        </div>
                      )}
                      {extraStop && (
                        <div className="flex justify-between">
                          <span>Extra tussenstop:</span>
                          <span className="font-semibold">+€7,50</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => totalPrice > 0 && setShowBookingForm(true)}
                  disabled={totalPrice === 0}
                  className="w-full py-4 bg-amber-500 text-white text-lg font-semibold rounded-xl hover:bg-amber-600 transition-all hover:shadow-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reserveer Nu <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showBookingForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 sm:px-8 py-6 flex justify-between items-center">
              <h3 className="text-xl sm:text-2xl font-bold text-slate-900">Reservering Afronden</h3>
              <button
                onClick={() => {
                  setShowBookingForm(false);
                  setSubmitStatus(null);
                }}
                className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form
              id="booking-calculator-form"
              data-readdy-form
              onSubmit={handleBookingSubmit}
              className="p-6 sm:p-8"
            >
              <div className="bg-amber-50 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-slate-900 mb-3">Uw Reservering:</h4>
                <div className="space-y-2 text-sm text-slate-700">
                  <p><strong>Route:</strong> {from} → {getAirportDisplayName(to)}</p>
                  <p><strong>Passagiers:</strong> {passengers}</p>
                  <p><strong>Totaalprijs:</strong> €{totalPrice.toFixed(2)}</p>
                </div>
              </div>

              {/* Hidden fields voor reserveringsdetails */}
              <input type="hidden" name="route" value={`${from} → ${getAirportDisplayName(to)}`} />
              <input type="hidden" name="bestemming" value={getAirportDisplayName(to)} />
              <input type="hidden" name="aantal_passagiers" value={passengers} />
              <input type="hidden" name="totaalprijs" value={`€${totalPrice.toFixed(2)}`} />
              <input type="hidden" name="stationwagon" value={stationWagon ? 'Ja (+€7,50)' : 'Nee'} />
              <input type="hidden" name="kinderzitjes" value={childSeats > 0 ? `${childSeats}x (+€${(childSeats * 5).toFixed(2)})` : 'Geen'} />
              <input type="hidden" name="extra_tussenstop" value={extraStop ? 'Ja (+€7,50)' : 'Nee'} />

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Naam *
                  </label>
                  <input
                    type="text"
                    name="naam"
                    required
                    minLength={2}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    E-mailadres *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Telefoonnummer *
                  </label>
                  <input
                    type="tel"
                    name="telefoon"
                    required
                    pattern="[+]?[0-9\s-]{10,}"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Ophaaladres *
                  </label>
                  <input
                    type="text"
                    name="ophaaladres"
                    required
                    minLength={5}
                    placeholder="Straat + huisnummer"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Postcode *
                    </label>
                    <input
                      type="text"
                      name="postcode"
                      required
                      pattern="[0-9]{4}\s?[A-Za-z]{2}"
                      placeholder="1234 AB"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Plaats *
                    </label>
                    <input
                      type="text"
                      name="plaats"
                      required
                      placeholder="Utrecht"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Datum *
                    </label>
                    <input
                      type="date"
                      name="datum"
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Tijd *
                    </label>
                    <input
                      type="time"
                      name="tijd"
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Vluchtnummer (indien van toepassing)
                  </label>
                  <input
                    type="text"
                    name="vluchtnummer"
                    placeholder="Bijv. KL1234"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Opmerkingen
                  </label>
                  <textarea
                    name="opmerkingen"
                    rows={3}
                    maxLength={500}
                    placeholder="Eventuele bijzonderheden of wensen"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                  ></textarea>
                  <p className="text-xs text-slate-500 mt-1">Maximaal 500 tekens</p>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-green-500 rounded-xl text-green-800">
                  <div className="flex items-start gap-3">
                    <i className="ri-check-circle-fill text-2xl text-green-500"></i>
                    <div>
                      <p className="font-bold mb-1">✅ Je reservering is verstuurd!</p>
                      <p className="text-sm">We nemen zo snel mogelijk contact met je op.</p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border-2 border-red-500 rounded-xl text-red-800">
                  <div className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-2xl text-red-500"></i>
                    <div className="flex-1">
                      <p className="font-bold mb-1">❌ Er ging iets mis bij het verzenden.</p>
                      <p className="text-sm">Probeer het opnieuw of bel/WhatsApp ons op 06 5882 4429.</p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-6 py-4 bg-amber-500 text-white text-lg font-semibold rounded-xl hover:bg-amber-600 transition-all hover:shadow-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Bezig met verzenden...
                  </>
                ) : (
                  <>
                    Bevestig Reservering <i className="ri-check-line ml-2"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
